import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Alert,
} from "@mui/material";
import api from "../../services/api/axios";
import { BRAND_BLUE } from "../../theme/colors";

function formatDateTime(val) {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function StatusChip({ status }) {
  if (!status) return <Chip size="small" label="Never" variant="outlined" />;
  if (status === "success") return <Chip size="small" label="Success" color="success" />;
  if (status === "failed") return <Chip size="small" label="Failed" color="error" />;
  return <Chip size="small" label={status} variant="outlined" />;
}

function InactiveChip({ days }) {
  if (days === null || days === undefined) return <Chip size="small" label="No data" variant="outlined" />;
  if (days >= 30) return <Chip size="small" label="30+ days" color="error" />;
  if (days >= 14) return <Chip size="small" label="14+ days" color="warning" />;
  if (days >= 7) return <Chip size="small" label="7+ days" color="warning" variant="outlined" />;
  return <Chip size="small" label={`${days}d`} color="success" />;
}

function SummaryCard({ title, value, subtitle }) {
  return (
    <Paper
      sx={{
        p: 2,
        border: `2px solid ${BRAND_BLUE}`,
        borderRadius: 4,
        textAlign: "center",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography variant="h4" sx={{ color: BRAND_BLUE, fontWeight: 700 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: BRAND_BLUE, fontWeight: 600 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
}

export default function AdminMetrics() {
  const [syncRows, setSyncRows] = useState([]);
  const [usageRows, setUsageRows] = useState([]);

  const [error, setError] = useState("");
  const [loadingSync, setLoadingSync] = useState(true);
  const [loadingUsage, setLoadingUsage] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/metrics/website-visits-sync");
        setSyncRows(res.data || []);
      } catch (e) {
        setError(
          (prev) =>
            prev || e?.response?.data?.detail || "Failed to load sync status",
        );
      } finally {
        setLoadingSync(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const usageRes = await api.get("/admin/metrics/crm-usage");
        setUsageRows(usageRes.data || []);
      } catch (e) {
        setError(
          (prev) =>
            prev || e?.response?.data?.detail || "Failed to load CRM usage",
        );
      } finally {
        setLoadingUsage(false);
      }
    })();
  }, []);

  const totalLogins = usageRows.reduce((s, r) => s + (r.total_logins || 0), 0);
  const totalUsageHours = usageRows.reduce((s, r) => s + (r.total_usage_hours || 0), 0);
  const activeOrgs30d = usageRows.filter((r) => r.logins_30d > 0).length;
  const inactiveOrgs30d = usageRows.filter((r) => r.inactive_30d).length;

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ color: BRAND_BLUE }}>
        Admin Metrics
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* =========================
          CRM Usage Summary Cards
      ========================== */}
      <Typography
        variant="subtitle1"
        sx={{ mt: 2, fontWeight: 700, color: BRAND_BLUE }}
      >
        CRM Usage
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mt: 0.5, mb: 2 }}>
        <Box sx={{ flex: 1, display: "flex" }}>
          <SummaryCard title="Total Logins" value={totalLogins.toLocaleString()} />
        </Box>
        <Box sx={{ flex: 1, display: "flex" }}>
          <SummaryCard
            title="Total Usage"
            value={`${totalUsageHours.toFixed(1)}h`}
            subtitle="estimated"
          />
        </Box>
        <Box sx={{ flex: 1, display: "flex" }}>
          <SummaryCard
            title="Active Orgs (30d)"
            value={activeOrgs30d}
            subtitle="with at least 1 login"
          />
        </Box>
        <Box sx={{ flex: 1, display: "flex" }}>
          <SummaryCard
            title="Inactive Orgs (30d)"
            value={inactiveOrgs30d}
            subtitle="no logins in 30 days"
          />
        </Box>
      </Box>

      {/* =========================
          Usage by Organization
      ========================== */}
      <Typography
        variant="subtitle1"
        sx={{ mt: 2, fontWeight: 700, color: BRAND_BLUE }}
      >
        Usage by Organization
      </Typography>

      <Paper sx={{ mt: 1, border: `2px solid ${BRAND_BLUE}`, borderRadius: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: BRAND_BLUE }}>Organization</TableCell>
              <TableCell align="right" sx={{ color: BRAND_BLUE }}>Total Logins</TableCell>
              <TableCell align="right" sx={{ color: BRAND_BLUE }}>Logins (30d)</TableCell>
              <TableCell align="right" sx={{ color: BRAND_BLUE }}>Active Users (30d)</TableCell>
              <TableCell align="right" sx={{ color: BRAND_BLUE }}>Avg Session</TableCell>
              <TableCell align="right" sx={{ color: BRAND_BLUE }}>Total Usage</TableCell>
              <TableCell sx={{ color: BRAND_BLUE }}>Last Active</TableCell>
              <TableCell sx={{ color: BRAND_BLUE }}>Inactivity</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usageRows.map((r) => (
              <TableRow key={r.organization_id}>
                <TableCell sx={{ color: BRAND_BLUE }}>{r.organization_name}</TableCell>
                <TableCell align="right" sx={{ color: BRAND_BLUE }}>{r.total_logins}</TableCell>
                <TableCell align="right" sx={{ color: BRAND_BLUE }}>{r.logins_30d}</TableCell>
                <TableCell align="right" sx={{ color: BRAND_BLUE }}>{r.active_users_30d}</TableCell>
                <TableCell align="right" sx={{ color: BRAND_BLUE }}>
                  {r.avg_session_minutes > 0 ? `${r.avg_session_minutes}m` : "—"}
                </TableCell>
                <TableCell align="right" sx={{ color: BRAND_BLUE }}>
                  {r.total_usage_hours > 0 ? `${r.total_usage_hours}h` : "—"}
                </TableCell>
                <TableCell sx={{ color: BRAND_BLUE }}>
                  {formatDateTime(r.last_active_at)}
                </TableCell>
                <TableCell>
                  <InactiveChip days={r.days_inactive} />
                </TableCell>
              </TableRow>
            ))}
            {!loadingUsage && usageRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No usage data yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* =========================
          Website Visits Sync Status
      ========================== */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE }}>
          Website Visits Sync Status
        </Typography>
        <Typography variant="body2" color="text.secondary">
          (Nightly cron)
        </Typography>
      </Stack>

      <Paper
        sx={{ mt: 1, border: `2px solid ${BRAND_BLUE}`, borderRadius: 4 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: BRAND_BLUE }}>Organization</TableCell>
              <TableCell sx={{ color: BRAND_BLUE }}>Last Synced</TableCell>
              <TableCell sx={{ color: BRAND_BLUE }}>Status</TableCell>
              <TableCell sx={{ color: BRAND_BLUE }}>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {syncRows.map((r) => {
              const msg = r.website_visits_last_sync_message || "";
              const msgShort =
                msg.length > 80 ? msg.slice(0, 80).trim() + "…" : msg;

              return (
                <TableRow key={r.organization_id}>
                  <TableCell sx={{ color: BRAND_BLUE }}>
                    {r.organization_name}
                  </TableCell>
                  <TableCell sx={{ color: BRAND_BLUE }}>
                    {formatDateTime(r.website_visits_last_synced_at)}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={r.website_visits_last_sync_status} />
                  </TableCell>
                  <TableCell sx={{ color: BRAND_BLUE }}>
                    {msg ? (
                      <Tooltip title={msg} arrow>
                        <span>{msgShort}</span>
                      </Tooltip>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            {!loadingSync && syncRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No sync status found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
